import { Fragment } from 'react';

import { ChevronRightIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Flex, IconButton, Separator, Text, Tooltip } from '@raystack/apsara';
import clsx from 'clsx';

import styles from './Header.module.css';

interface HeaderProps {
  children: React.ReactNode;
  onClose?: React.MouseEventHandler;
  hasSeparator?: boolean;
  className?: string;
  disabled?: boolean;
  actions?: React.ReactNode;
}

interface HeaderText {
  children: React.ReactNode;
  size?: React.ComponentProps<typeof Text>['size'];
  onClick?: () => void;
}

interface HeaderBreadcrumbs {
  list: { text: string; onClick?: HeaderText['onClick'] }[];
  size?: HeaderText['size'];
}

const HeaderText = ({ children, onClick, size = 4 }: HeaderText) => {
  return (
    <Text
      data-test-id={`text-${children}`}
      size={size}
      onClick={onClick}
      className={clsx(onClick && styles.textClick)}
    >
      {children}
    </Text>
  );
};

export const HEADER_ICON_COLOR = 'var(--rs-color-foreground-base-secondary)';

const HeaderBreadcrumbs = ({ list, size }: HeaderBreadcrumbs) => {
  const listLen = list.length - 1;

  return (
    <Flex gap='small' align='center' className={styles.breadcrumbs}>
      {list.map(({ text, onClick }, idx) => (
        <Fragment key={idx}>
          <HeaderText
            data-test-id={`text-${text}`}
            size={size}
            onClick={onClick}
          >
            {text}
          </HeaderText>

          {idx !== listLen && list[idx + 1]?.text && (
            <ChevronRightIcon className={styles.icon} />
          )}
        </Fragment>
      ))}
    </Flex>
  );
};

const HeaderRoot = ({
  children,
  onClose,
  hasSeparator = true,
  disabled,
  actions,
  className
}: HeaderProps) => {
  return (
    <>
      <Flex
        align='center'
        justify='between'
        className={clsx(styles.header, className)}
      >
        {children}

        <Flex gap='small' align='center'>
          {actions}

          {onClose && (
            <IconButton
              size={3}
              data-test-id='close-header-btn'
              onClick={onClose}
              disabled={disabled}
            >
              <Tooltip message='Close' side='bottom'>
                <Cross2Icon color={HEADER_ICON_COLOR} className={styles.icon} />
              </Tooltip>
            </IconButton>
          )}
        </Flex>
      </Flex>

      {hasSeparator && <Separator />}
    </>
  );
};

export const Header = Object.assign(HeaderRoot, {
  Text: HeaderText,
  Breadcrumbs: HeaderBreadcrumbs
});
